import React, { useState } from 'react'
import { Box, Card, CardContent, Tab, Tabs, IconButton } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { useFormFactor } from '../../../../hooks'
import { CONTENT_CARD_WIDTH } from '../../../../components/general-card'
import { HomeContentCard } from '../home-content-card'
import { useScrollableRails } from '../../hooks/useScrollableRails'
import { HomeRailsScrollableContainer } from '../home-rails-scrollable-container'
import { HomeRails } from '../home-rails'
import { HOME_SECTION_TYPE } from './home-sections'

function TabPanel (props) {
  const { children, selected, value, ...other } = props

  return (
    <Box
      role='tabpanel'
      hidden={selected !== value}
      id={`home-whats-new-tabpanel-${value}`}
      aria-labelledby={`home-whats-new-tab-${value}`}
      {...other}
    >
      {selected === value && <Box sx={{ pt: 2 }}>{children}</Box>}
    </Box>
  )
}

function a11yProps (index) {
  return {
    id: `home-whats-new-tab-${index}`,
    'aria-controls': `home-whats-new-tabpanel-${index}`
  }
}

// Component for tabbed layout (when user has favorite topics)
const WhatsNewRailsWithTabs = ({ whatsNewSection }) => {
  const { children } = whatsNewSection
  const [selectedTab, setSelectedTab] = useState(children[0]?.id)
  const { isMobile } = useFormFactor()

  const selectedCategory = children.find(child => child.id === selectedTab)

  const dataTest = 'home-whats-new-rails'

  const {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    hasScrollableContent,
    scrollLeft,
    scrollRight,
    updateScrollState
  } = useScrollableRails(selectedCategory?.items || [])

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue)
  }

  return (
    <Card data-test={dataTest} sx={{ backgroundColor: (theme) => theme.palette.grey.shade[3] }}>
      <CardContent sx={{ m: 0, p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', p: 2, backgroundColor: (theme) => theme.palette.grey.shade[7], alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E0E0E0' }}>
          <Box sx={{ fontWeight: 700 }}>What&apos;s New</Box>
        </Box>
        <Box sx={{ display: 'flex', alignContent: 'center', justifyContent: 'space-between' }}>
          <Tabs
            value={selectedTab}
            onChange={handleChange}
            aria-label="what's new category tabs"
            sx={{
              borderBottom: 'none',
              '.MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3
              }
            }}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
          >
            {children.map((child, index) => (
              <Tab
                key={child.id}
                label={child.title}
                {...a11yProps(index)}
                value={child.id}
              />
            ))}
          </Tabs>
          {!isMobile && hasScrollableContent && (
            <Box display='flex' alignItems='center' data-test={`${dataTest}-scroll-buttons-container`} justifyContent='end'>
              {/* Left scroll arrow */}
              <IconButton
                onClick={scrollLeft}
                size='small'
                disabled={!canScrollLeft}
                data-test={`${dataTest}-scroll-left-button`}
              >
                <ChevronLeft />
              </IconButton>

              {/* Right scroll arrow */}
              <IconButton
                onClick={scrollRight}
                size='small'
                data-test={`${dataTest}-scroll-right-button`}
                disabled={!canScrollRight}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}
        </Box>
        {children.map((child, index) => (
          <TabPanel key={child.id} selected={selectedTab} value={child.id}>
            <Box sx={{ position: 'relative' }}>
              <HomeRailsScrollableContainer
                data-test={`${dataTest}-scrollable-container`}
                scrollContainerRef={scrollContainerRef}
                onScroll={updateScrollState}
              >
                {child.items.map((item, itemIndex) => (
                  <Box
                    key={item.sk}
                    sx={{
                      minWidth: CONTENT_CARD_WIDTH,
                      maxWidth: CONTENT_CARD_WIDTH,
                      flexShrink: 0
                    }}
                  >
                    <HomeContentCard
                      content={item}
                      itemIndex={itemIndex}
                      extraTrackingData={{
                        categoryId: child.id,
                        categoryIndex: index,
                        categoryTitle: child.title
                      }}
                      dataTest={`home-whats-new-${index}-item-${itemIndex}`}
                      railType={HOME_SECTION_TYPE.WHATS_NEW}
                    />
                  </Box>
                ))}
              </HomeRailsScrollableContainer>
            </Box>
          </TabPanel>
        ))}
      </CardContent>
    </Card>
  )
}

// Component for simple flat layout (when user has no favorite topics)
const WhatsNewRailsSimple = ({ items }) => {
  return (
    <HomeRails
      title="What's New"
      items={items}
      dataTest='home-whats-new-rails'
      renderItem={(item, itemIndex) => {
        return (
          <HomeContentCard
            content={item}
            dataTest={`home-whats-new-${itemIndex}`}
            itemIndex={itemIndex}
            railType={HOME_SECTION_TYPE.WHATS_NEW}
          />
        )
      }}
    />
  )
}

// Main component that decides which layout to render
export const WhatsNewRails = ({ whatsNewSection }) => {
  const { items, children } = whatsNewSection

  // If children exist, render with tabs (new behavior)
  if (children && children.length > 0) {
    return <WhatsNewRailsWithTabs whatsNewSection={whatsNewSection} />
  }

  // Otherwise, render current simple version (fallback)
  return <WhatsNewRailsSimple items={items} />
}
